import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useCountyApi } from '../../services/api/useCounty.api';
import { useTeamApi } from '../../services/api/useTeam.api';
import { useUserApi } from '../../services/api/useUser.api';
import { ApiResponse } from '../../types/ApiResponse.type';
import { County } from '../../types/County.type';
import { FeatureFlag } from '../../types/FeatureFlag/FeatureFlag.type';
import { FeatureFlagAccess } from '../../types/FeatureFlag/FeatureFlagUpdate.type';
import { Team } from '../../types/Team/Team.type';
import { User } from '../../types/User/User.type';
import InputMultiSelect from './InputMultiSelect.tsx';

interface FeatureFlagAccessModalProps {
  open: boolean;
  onClose: () => void;
  flag: FeatureFlag;
  loading: boolean;
  onSave: (access: FeatureFlagAccess) => void;
}

const sameIds = (a: { id: number }[], b: { id: number }[]) => {
  if (a.length !== b.length) return false;
  const bIds = new Set(b.map((item) => item.id));
  return a.every((item) => bIds.has(item.id));
};

const userLabel = (user: User) => {
  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
  return fullName ? `${fullName} (${user.email})` : user.email;
};

function FeatureFlagAccessModal({ open, onClose, flag, loading, onSave }: FeatureFlagAccessModalProps) {
  const { getCounties } = useCountyApi();
  const { getTeams } = useTeamApi();
  const { getUsers } = useUserApi();

  const [selectedCounties, setSelectedCounties] = useState<County[]>(flag.counties);
  const [selectedTeams, setSelectedTeams] = useState<Team[]>(flag.teams);
  const [selectedUsers, setSelectedUsers] = useState<User[]>(flag.users);
  const [userSearch, setUserSearch] = useState('');

  const debouncedUserSearch = useDebouncedValue(userSearch, 400);

  // reset modal on re-open
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setSelectedCounties(flag.counties);
      setSelectedTeams(flag.teams);
      setSelectedUsers(flag.users);
      setUserSearch('');
    }
  }

  const { data: counties, isLoading: countiesLoading } = useQuery({
    queryKey: ['counties'],
    queryFn: () => getCounties({ is_active: true }),
    enabled: open,
  });

  const {
    data: teamsData,
    isLoading: teamsLoading,
    fetchNextPage: fetchNextTeams,
    hasNextPage: hasNextTeams,
    isFetchingNextPage: isFetchingNextTeams,
  } = useInfiniteQuery({
    queryKey: ['teams'],
    queryFn: ({ pageParam = 1 }) => getTeams({ page: pageParam, per_page: 40, sort: 'name:asc' }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<Team[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
    enabled: open,
  });

  const teams = teamsData?.pages?.flatMap((page) => page.data) ?? [];

  const {
    data: usersData,
    isLoading: usersLoading,
    fetchNextPage: fetchNextUsers,
    hasNextPage: hasNextUsers,
    isFetchingNextPage: isFetchingNextUsers,
  } = useInfiniteQuery({
    queryKey: ['featureFlagUsers', debouncedUserSearch],
    queryFn: ({ pageParam = 1 }) =>
      getUsers({ page: pageParam, per_page: 20, sort: 'email:asc', query: debouncedUserSearch || undefined }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<User[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
    enabled: open,
  });

  const users = usersData?.pages?.flatMap((page) => page.data) ?? [];

  const hasChanges =
    !sameIds(selectedCounties, flag.counties) ||
    !sameIds(selectedTeams, flag.teams) ||
    !sameIds(selectedUsers, flag.users);

  const handleSave = () => {
    if (!hasChanges) return;

    onSave({
      team_ids: selectedTeams.map((team) => team.id),
      county_ids: selectedCounties.map((county) => county.id),
      user_ids: selectedUsers.map((user) => user.id),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (e: React.FormEvent) => {
            e.preventDefault();
            handleSave();
          },
        },
      }}
    >
      <DialogTitle>Edit visibility</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Stack sx={{ backgroundColor: '#F7FAFC', padding: 2, borderRadius: 1 }} spacing={0.5}>
            <Typography variant='body1' sx={{ color: 'text.main', fontWeight: 'medium' }}>
              {flag.name}
            </Typography>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              {flag.description || 'No description'}
            </Typography>
          </Stack>
          <InputMultiSelect
            options={counties ?? []}
            value={selectedCounties}
            onChange={setSelectedCounties}
            getLabel={(county) => county.name}
            label='Counties'
            placeholder='Select counties'
            loading={countiesLoading}
          />
          <InputMultiSelect
            options={teams}
            value={selectedTeams}
            onChange={setSelectedTeams}
            getLabel={(team) => team.name}
            label='Teams'
            placeholder='Select teams'
            loading={teamsLoading || isFetchingNextTeams}
            onScrollEnd={() => {
              if (hasNextTeams && !isFetchingNextTeams) {
                fetchNextTeams();
              }
            }}
          />
          <InputMultiSelect
            options={users}
            value={selectedUsers}
            onChange={setSelectedUsers}
            getLabel={userLabel}
            label='Users'
            placeholder='Search users by name or email'
            loading={usersLoading || isFetchingNextUsers}
            noOptionsText={userSearch ? 'No users found' : 'Start typing to search users'}
            inputValue={userSearch}
            onInputChange={setUserSearch}
            onScrollEnd={() => {
              if (hasNextUsers && !isFetchingNextUsers) {
                fetchNextUsers();
              }
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant='contained' disableElevation disabled={loading || !hasChanges} onClick={handleSave}>
          {loading ? 'Please wait...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FeatureFlagAccessModal;
